import {
  Stack,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { format, isLastDayOfMonth } from 'date-fns';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EnumCurrency } from 'tdc-web-backend/enums/enums';
import CustomNoRowsOverlay from '../../../../components/custom-no-rows-overlay/CustomNoRowsOverlay';
import theme from '../../../../utils/theme';
import {
  convertToObject,
  getAllDaysInCurrentMonth,
  getAllYearDatesInterval,
  getSixMonthsInterval,
} from './utils';
import { AreaDotRadius, IGraphMilestone, IntervalType, IRevenueChartProps } from './interface';
import CustomAreaChart from './components/CustomAreaChart/CustomAreaChart';
import CustomBarChart from './components/CustomBarChart/CustomBarChart';
import { primaryLight, secondaryGreen, secondaryOrange } from '../../../../utils/color';
import CustomTooltip from '../../../../components/tooltip/CustomTooltip';
import ChartTooltip from './components/components/ChartTooltip';

const toggleButtonStyles: SxProps<Theme> = {
  py: 0.1,
  px: 1.2,
  border: 'none',
  color: 'primaryDark.500',
  fontWeight: 600,
};

const toggleButtonActiveStyles: SxProps<Theme> = {
  color: 'primaryLight.500',
  boxShadow: '6px 6px 15px rgba(56, 69, 114, 0.1)',
};

const intervalDateFunctionMap = {
  month: getAllDaysInCurrentMonth,
  halfyear: getSixMonthsInterval,
  year: getAllYearDatesInterval,
};

const RevenueChart = ({ type = 'area', milestones, sx }: IRevenueChartProps) => {
  const [array, setArray] = useState<IGraphMilestone[]>([]);
  const [intervalType, setIntervalType] = useState<IntervalType>('month');
  const [highestValueField, setHighestValueField] = useState<string>('');
  const [showPaidEur, setShowPaidEur] = useState<boolean>(true);
  const [showPaidUsd, setShowPaidUsd] = useState<boolean>(true);
  const [showUnpaidEur, setShowUnpaidEur] = useState<boolean>(true);
  const [showUnpaidUsd, setShowUnpaidUsd] = useState<boolean>(true);
  const [allArePaidEur, setAllArePaidEur] = useState<boolean>(true);
  const [allArePaidUsd, setAllArePaidUsd] = useState<boolean>(true);
  const [milestonesExistEur, setMilestonesExistEur] = useState<boolean>(false);
  const [milestonesExistUsd, setMilestonesExistUsd] = useState<boolean>(false);
  const [responsiveContainerWidth, setResponsiveContainerWidth] = useState('100%');
  const isGreaterThanLarge = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    if (milestones) {
      const allIntervalDates = convertToObject(intervalDateFunctionMap[intervalType]());
      const highestValue = { value: 0, field: '' };
      let allPaidEur = true;
      let allPaidUsd = true;
      let milestonesExistEur = false;
      let milestonesExistUsd = false;

      milestones.forEach((milestone) => {
        const milestonePaidDate = milestone.paid
          ? format(new Date(milestone.paid) as Date, 'yyyy-MM-dd')
          : null;

        if (milestonePaidDate && allIntervalDates[milestonePaidDate]) {
          if (milestone.currency === EnumCurrency.Usd) {
            milestonesExistUsd = true;
            allIntervalDates[milestonePaidDate].paidUsd += milestone.budget;
            allIntervalDates[milestonePaidDate].paidMilestonesUsd.push(milestone.name);

            if (allIntervalDates[milestonePaidDate].paidUsd > highestValue.value) {
              highestValue.value = allIntervalDates[milestonePaidDate].paidUsd;
              highestValue.field = 'paidUsd';
            }
          } else if (milestone.currency === EnumCurrency.Eur) {
            milestonesExistEur = true;
            allIntervalDates[milestonePaidDate].paidEur += milestone.budget;
            allIntervalDates[milestonePaidDate].paidMilestonesEur.push(milestone.name);
            if (allIntervalDates[milestonePaidDate].paidEur > highestValue.value) {
              highestValue.value = allIntervalDates[milestonePaidDate].paidEur;
              highestValue.field = 'paidEur';
            }
          }
        } else if (!milestonePaidDate) {
          const milestoneExpectedDate = format(new Date(milestone.end) as Date, 'yyyy-MM-dd');
          if (allIntervalDates[milestoneExpectedDate]) {
            if (milestone.currency === EnumCurrency.Usd) {
              milestonesExistUsd = true;
              allPaidUsd = false;
              allIntervalDates[milestoneExpectedDate].unpaidUsd += milestone.budget;
              allIntervalDates[milestoneExpectedDate].unpaidMilestonesUsd.push(milestone.name);
              if (allIntervalDates[milestoneExpectedDate].unpaidUsd > highestValue.value) {
                highestValue.value = allIntervalDates[milestoneExpectedDate].unpaidUsd;
                highestValue.field = 'unpaidUsd';
              }
            } else if (milestone.currency === EnumCurrency.Eur) {
              allPaidEur = false;
              milestonesExistEur = true;
              allIntervalDates[milestoneExpectedDate].unpaidEur += milestone.budget;
              allIntervalDates[milestoneExpectedDate].unpaidMilestonesEur.push(milestone.name);
              if (allIntervalDates[milestoneExpectedDate].unpaidEur > highestValue.value) {
                highestValue.value = allIntervalDates[milestoneExpectedDate].unpaidEur;
                highestValue.field = 'unpaidEur';
              }
            }
          }
        }
      });
      setArray(Object.values(allIntervalDates));
      setHighestValueField(highestValue.field);
      setAllArePaidEur(allPaidEur);
      setAllArePaidUsd(allPaidUsd);
      setMilestonesExistEur(milestonesExistEur);
      setMilestonesExistUsd(milestonesExistUsd);
    }
  }, [milestones, intervalType]);

  useEffect(() => {
    if (isGreaterThanLarge) {
      setResponsiveContainerWidth('100%');
    } else {
      setResponsiveContainerWidth('93%');
    }
  }, [isGreaterThanLarge]);

  return (
    <Stack sx={{ ...sx, position: 'relative' }} width="100%" height="100%" justifyContent="end">
      <ToggleButtonGroup
        color="secondary"
        value={intervalType}
        exclusive
        onChange={(e, newIntervalType) => {
          if (newIntervalType !== null) {
            setIntervalType(newIntervalType);
          }
        }}
        aria-label="Platform"
        sx={{
          position: 'absolute',
          top: -40,
          left: 0,
          right: 0,
          margin: 'auto',
          width: 'fit-content',
          gap: 0,
          border: `1px solid ${theme.palette.secondaryBlue[100]}`,
          justifyContent: 'center',
          display: milestones?.length ? 'block' : 'none',
        }}
      >
        <ToggleButton
          value="month"
          sx={{
            ...toggleButtonStyles,
            borderRight: `1px solid ${theme.palette.secondaryBlue[100]}`,
            '&.Mui-selected': { ...toggleButtonActiveStyles },
          }}
        >
          1M
        </ToggleButton>

        <ToggleButton
          value="halfyear"
          sx={{
            ...toggleButtonStyles,
            borderRight: `1px solid ${theme.palette.secondaryBlue[100]}`,
            '&.Mui-selected': { ...toggleButtonActiveStyles },
          }}
        >
          6M
        </ToggleButton>

        <ToggleButton
          value="year"
          sx={{ ...toggleButtonStyles, '&.Mui-selected': { ...toggleButtonActiveStyles } }}
        >
          1Y
        </ToggleButton>
      </ToggleButtonGroup>

      <ResponsiveContainer width={responsiveContainerWidth} height="90%">
        <AreaChart
          data={array.length > 0 ? array : []}
          style={{
            fontSize: '1rem',
            fontFamily: theme.typography.fontFamily,
            // marginLeft: -10,
          }}
          margin={{
            top: 20,
            right: 10,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="paidEur" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryLight[500]} stopOpacity={0.6} />
              <stop offset="100%" stopColor={primaryLight[500]} stopOpacity={0.1} />
            </linearGradient>

            <linearGradient id="unpaidEur" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryLight[300]} stopOpacity={0.6} />
              <stop offset="100%" stopColor={primaryLight[300]} stopOpacity={0} />
            </linearGradient>

            <linearGradient id="paidUsd" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={secondaryGreen[801]} stopOpacity={0.6} />
              <stop offset="100%" stopColor={secondaryGreen[801]} stopOpacity={0.1} />
            </linearGradient>

            <linearGradient id="unpaidUsd" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={secondaryGreen[601]} stopOpacity={0.6} />
              <stop offset="100%" stopColor={secondaryGreen[601]} stopOpacity={0} />
            </linearGradient>
          </defs>

          <Legend
            wrapperStyle={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '1rem',
              display: milestones?.length ? 'block' : 'none',
            }}
            verticalAlign="bottom"
            height={15}
            onClick={(a, b, c) => {
              const { value } = a;
              switch (value) {
                case 'paidEur':
                  setShowPaidEur(!showPaidEur);
                  break;
                case 'paidUsd':
                  setShowPaidUsd(!showPaidUsd);
                  break;
                case 'unpaidEur':
                  setShowUnpaidEur(!showUnpaidEur);
                  break;
                case 'unpaidUsd':
                  setShowUnpaidUsd(!showUnpaidUsd);
                  break;
                default:
                  break;
              }
            }}
            formatter={(value, entry, index) => {
              const { color } = entry;
              let newValue = value;
              let title = value;
              switch (value) {
                case 'paidEur':
                  newValue = 'P(€)';
                  title = 'Paid (€)';
                  break;
                case 'paidUsd':
                  newValue = 'P($)';
                  title = 'Paid ($)';
                  break;
                case 'unpaidEur':
                  newValue = 'U(€)';
                  title = 'Unpaid (€)';
                  break;
                case 'unpaidUsd':
                  newValue = 'U($)';
                  title = 'Unpaid ($)';
                  break;
                default:
                  break;
              }
              return (
                <CustomTooltip title={title}>
                  <Typography variant="body3" sx={{ color, cursor: 'pointer' }}>
                    {newValue}
                  </Typography>
                </CustomTooltip>
              );
            }}
          />

          <CartesianGrid opacity={0.3} vertical={false} />
          <Tooltip content={<ChartTooltip />} wrapperStyle={{ outline: 'none', zIndex: 35 }} />

          <YAxis
            dataKey={highestValueField}
            axisLine={false}
            tickLine={false}
            // tickCount={4}
            tick={{ fill: theme.palette.primaryDark[400] }}
            // this will return the currency in the k (kilo) format; e.g. $7.5k
            tickFormatter={(value) =>
              new Intl.NumberFormat('en-US', {
                notation: 'compact',
                maximumFractionDigits: 1,
              }).format(value)
            }
            dx={-5}
            style={{ fontSize: '0.875rem', fontWeight: 600 }}
          />

          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            padding={{ left: 25, right: 25 }}
            tick={{ fill: theme.palette.primaryDark[400] }}
            scale="point"
            tickMargin={15}
            height={50}
            // show all ticks
            interval={0}
            tickFormatter={(value) => {
              // value is date

              if (intervalType === 'month') {
                if (value.getDate() === 1) {
                  return format(value, 'MMM d').toUpperCase();
                }

                // if not february
                if (value.getMonth() !== 1) {
                  if (value.getDate() % 10 === 0 && value.getDate() !== 30) {
                    return format(value, 'MMM d').toUpperCase();
                  }

                  if (
                    value.getDate() % 10 === 0 &&
                    value.getDate() === 30 &&
                    isLastDayOfMonth(value)
                  ) {
                    return format(value, 'MMM d').toUpperCase();
                  }

                  if (value.getDate() === 31 && isLastDayOfMonth(value)) {
                    return format(value, 'MMM d').toUpperCase();
                  }
                }

                // if february
                if (value.getMonth() === 1) {
                  if (value.getDate() % 10 === 0) {
                    return format(value, 'MMM d').toUpperCase();
                  }

                  if (value.getDate() === 28 || value.getDate() === 29) {
                    return format(value, 'MMM d').toUpperCase();
                  }
                }
              }

              if (intervalType === 'halfyear') {
                if (value.getDate() === 1) {
                  return format(value, 'MMM d').toUpperCase();
                }
              }

              if (intervalType === 'year') {
                if (value.getMonth() === 0 && value.getDate() === 1) {
                  return format(value, 'MMM d').toUpperCase();
                }

                if (value.getMonth() === 3 && value.getDate() === 1) {
                  return format(value, 'MMM d').toUpperCase();
                }

                if (value.getMonth() === 6 && value.getDate() === 1) {
                  return format(value, 'MMM d').toUpperCase();
                }

                if (value.getMonth() === 9 && value.getDate() === 1) {
                  return format(value, 'MMM d').toUpperCase();
                }

                if (value.getMonth() === 11 && value.getDate() === 31) {
                  return format(value, 'MMM d').toUpperCase();
                }
              }

              return '';
            }}
            // style={{ fontSize: '0.875rem' }}
            style={{ fontSize: '0.875rem', fontWeight: 600 }}
          />
          {milestonesExistEur && (
            <Area
              display={showPaidEur ? 'block' : 'none'}
              dataKey="paidEur"
              legendType="circle"
              type="monotone"
              stroke={primaryLight[500]}
              fillOpacity={1}
              isAnimationActive={false}
              strokeWidth={1.5}
              fill="url(#paidEur)"
              activeDot={{ r: AreaDotRadius, stroke: primaryLight[700], fill: primaryLight[700] }}
              dot={(payload) => {
                if (payload) {
                  if (payload.payload.paidEur !== 0 && showPaidEur) {
                    if (intervalType === 'halfyear') return <></>;
                    if (intervalType === 'year') return <></>;
                    return (
                      <circle
                        key={payload.cx}
                        r={AreaDotRadius}
                        stroke={primaryLight[700]}
                        fill={primaryLight[700]}
                        strokeWidth={AreaDotRadius}
                        cx={payload.cx}
                        cy={payload.cy}
                      />
                    );
                  }

                  return <></>;
                }

                return <></>;
              }}
            />
          )}
          {!allArePaidEur && milestonesExistEur && (
            <Area
              display={showUnpaidEur ? 'block' : 'none'}
              dataKey="unpaidEur"
              legendType="circle"
              isAnimationActive={false}
              type="monotone"
              stroke={primaryLight[300]}
              fillOpacity={1}
              strokeWidth={1.5}
              fill="url(#unpaidEur)"
              activeDot={(payload) => {
                if (payload) {
                  if (payload.payload.unpaid !== 0 && showUnpaidEur) {
                    return (
                      <circle
                        key={payload.cx}
                        r={AreaDotRadius}
                        stroke={primaryLight[700]}
                        fill={primaryLight[700]}
                        strokeWidth={AreaDotRadius}
                        cx={payload.cx}
                        cy={payload.cy}
                      />
                    );
                  }

                  return <></>;
                }

                return <></>;
              }}
            />
          )}
          {milestonesExistUsd && (
            <Area
              display={showPaidUsd ? 'block' : 'none'}
              dataKey="paidUsd"
              legendType="circle"
              type="monotone"
              stroke={secondaryGreen[801]}
              fillOpacity={1}
              isAnimationActive={false}
              strokeWidth={1.5}
              fill="url(#paidUsd)"
              activeDot={{
                r: AreaDotRadius,
                stroke: secondaryGreen[901],
                fill: secondaryGreen[901],
              }}
              dot={(payload) => {
                if (payload) {
                  if (payload.payload.paidUsd !== 0 && showPaidUsd) {
                    if (intervalType === 'halfyear') return <></>;
                    if (intervalType === 'year') return <></>;
                    return (
                      <circle
                        key={payload.cx}
                        r={AreaDotRadius}
                        stroke={secondaryGreen[901]}
                        fill={secondaryGreen[901]}
                        strokeWidth={AreaDotRadius}
                        cx={payload.cx}
                        cy={payload.cy}
                      />
                    );
                  }

                  return <></>;
                }

                return <></>;
              }}
            />
          )}

          {!allArePaidUsd && milestonesExistUsd && (
            <Area
              display={showUnpaidUsd ? 'block' : 'none'}
              dataKey="unpaidUsd"
              legendType="circle"
              isAnimationActive={false}
              type="monotone"
              stroke={secondaryGreen[701]}
              fillOpacity={1}
              strokeWidth={1.5}
              fill="url(#unpaidUsd)"
              activeDot={(payload) => {
                if (payload) {
                  if (payload.payload.unpaid !== 0 && showUnpaidUsd) {
                    return (
                      <circle
                        key={payload.cx}
                        r={AreaDotRadius}
                        stroke={secondaryGreen[901]}
                        fill={secondaryGreen[901]}
                        strokeWidth={AreaDotRadius}
                        cx={payload.cx}
                        cy={payload.cy}
                      />
                    );
                  }

                  return <></>;
                }

                return <></>;
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>

      {!milestones?.length && <CustomNoRowsOverlay />}
    </Stack>
  );
};

export default memo(RevenueChart);
